import { useMemo } from 'react';
// eslint-disable-next-line no-restricted-imports
import { Link as RRLink, Navigate as RRNavigate, useNavigate, type NavigateOptions } from 'react-router-dom';

type ParsedUrlQueryInput = NodeJS.Dict<
	string | number | boolean | readonly string[] | readonly number[] | readonly boolean[] | null
>;

interface UrlObject {
	auth?: string;
	hash?: string;
	host?: string;
	hostname?: string;
	href?: string;
	pathname?: string;
	protocol?: string;
	search?: string;
	slashes?: boolean;
	port?: string | number;
	query?: string | ParsedUrlQueryInput;
}

export const useRouter = () => {
	const navigate = useNavigate();

	const routerMethods = useMemo(() => {
		return {
			/**
			 * Navigate to a new route by pushing it to the history stack.
			 *
			 * @param path - Target URL path
			 * @param options.state - (Deprecated) This parameter is deprecated. Use query strings or a global store instead.
			 */
			push: (path: string | UrlObject, options?: Omit<NavigateOptions, 'replace'>) => navigate(path, options),
			/**
			 * Navigate to a new route by replacing the current entry in the history stack.
			 *
			 * @param path - Target URL path
			 * @param options.state - (Deprecated) This parameter is deprecated. Use query strings or a global store instead.
			 */
			replace: (path: string | UrlObject, options?: Omit<NavigateOptions, 'replace'>) =>
				navigate(path, { replace: true, ...options }),
			back: () => navigate(-1),
			go: (n: number) => navigate(n)
		};
	}, [navigate]);

	return routerMethods;
};

export const Link = ({
	href,
	replace,
	state,
	...rest
}: { href: string | UrlObject; replace?: boolean; state?: unknown } & Omit<
	React.AnchorHTMLAttributes<HTMLAnchorElement>,
	'href'
>) => {
	return <RRLink to={href} replace={replace} state={state} {...rest} />;
};

export const Navigate = ({ to, replace, state }: { to: string | UrlObject; replace?: boolean; state?: unknown }) => {
	return <RRNavigate to={to} replace={replace} state={state} />;
};
